import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Search } from 'lucide-react';
import axios from 'axios';
import { Switch } from '@headlessui/react'; // For toggle switch
import './dashboardPage.css';

const ITEMS_PER_PAGE = 30; // Number of points per page

const Trends = ({keyword, setKeyword}) => {
  const [aggregation, setAggregation] = useState('daily'); // Default to daily aggregation
  const [trendData, setTrendData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showZeroPages, setShowZeroPages] = useState(false); // State for toggling zero pages

  // Fetch trend data from the API
  const fetchTrendData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BEURL}/tweets/temporal_distribution`, {
        params: { keyword, aggregation },
      });

      const data = response.data.map(item => ({
        timestamp: item.key, // Raw timestamp
        displayDate: aggregation === 'daily'
          ? new Date(item.key).toLocaleDateString() // Format as a date for daily
          : new Date(item.key).toLocaleString(), // Format as a full timestamp for hourly
        displayLabel: aggregation === 'daily'
          ? new Date(item.key).toLocaleDateString('en-US', { weekday: 'short' }) // Day name for daily
          : new Date(item.key).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), // Hour for hourly
        count: item.doc_count, // Count value
      }));

      setTrendData(data);
    } catch (error) {
      console.error('Error fetching trend data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch data whenever keyword or aggregation changes
  useEffect(() => {
    fetchTrendData();
    setCurrentPage(1); // Reset to the first page on new data
  }, [keyword, aggregation]);

  // Paginated Data
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedData = trendData.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const totalPages = Math.ceil(trendData.length / ITEMS_PER_PAGE);

  // Function to find the next page with non-zero values
  const findNextNonZeroPage = (current, direction) => {
    const step = direction === 'next' ? 1 : -1;
    let newPage = current + step;

    while (newPage >= 1 && newPage <= totalPages) {
      const start = (newPage - 1) * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE;
      const hasNonZero = trendData.slice(start, end).some(item => item.count > 0);

      if (hasNonZero || showZeroPages) return newPage;
      newPage += step;
    }

    return current; // Return the current page if no valid page is found
  };

  // Handle Page Change with Skip Logic
  const handlePageChange = direction => {
    setCurrentPage(prev => findNextNonZeroPage(prev, direction));
  };

  const handleAggregationChange = event => {
    setAggregation(event.target.value);
  };

  return (
    <div className="container mx-auto px-6 py-8">

      {/* Trend Analysis */}
      <div className="mb-8">
        <div className="card p-6 mb-8 mt-4">
          <h2 className="text-xl font-semibold mb-4 gradient-text">Trend Analysis</h2>

          {/* Aggregation Selector */}
          <div className="mb-4">
            <label htmlFor="aggregation" className="mr-2">
              Select Aggregation:
            </label>
            <select
              id="aggregation"
              value={aggregation}
              onChange={handleAggregationChange}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="daily">Daily</option>
              <option value="hourly">Hourly</option>
            </select>
          </div>

          {/* Zero Pages Toggle */}
          <div className="mb-4 flex items-center">
            <span className="mr-2">Show with zero :</span>
            <Switch
              checked={showZeroPages}
              onChange={setShowZeroPages}
              className={`${
                showZeroPages ? 'bg-indigo-500' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full`}
            >
              <span
                className={`${
                  showZeroPages ? 'translate-x-5' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition`}
              />
            </Switch>
          </div>

          {/* Paginated Line Chart */}
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={paginatedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="displayLabel"
                  interval={0} // Show all labels in paginated view
                  tick={{ angle: -45, textAnchor: 'end' }} // Rotate labels
                />
                <YAxis domain={[0, 'auto']} />
                <Tooltip
                  labelFormatter={label =>
                    paginatedData.find(item => item.displayLabel === label)?.displayDate || label
                  }
                  formatter={(value, name) => [value, name === 'count' ? 'Tweet Count' : name]}
                />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#8884d8" activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Pagination Controls */}
          <div className="mt-4 flex justify-between">
            <button
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-900 focus:outline-none disabled:bg-gray-300 disabled:text-gray-500"
              onClick={() => handlePageChange('prev')}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="px-4 py-2">
             {currentPage} of {totalPages}
            </span>
            <button
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-900 focus:outline-none disabled:bg-gray-300 disabled:text-gray-500"
              onClick={() => handlePageChange('next')}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Trends;
